import { Map } from 'immutable';
import { isEmpty } from 'lodash';

import type { RecordOfObjectsState } from '../objects/types';
import type { MatchReducerState, RecordOfMatchesState, SSRMatchesState } from './types';

import { LanguageStore } from 'app/lang';

import {
  addMatchIdToObject,
  clearEmptyObjects,
  createObjectsState,
  removeMatchIdFromObjects,
} from '../objects';
import { initialState as objectInitialState } from '../objects/records';
import { stakesReducer, transformStakesSSRState, updateStakesNames } from '../stakes';
import type { BackendEntities } from '../types/backendEntities';
import { transformNodeSSRState, updateField } from '../utils';
import { initialState, MatchEntity } from './records';
import { transformMatch } from './transform';
import {
  createImmutableMatchObjects,
  createImmutableMatchPeriods,
  createImmutableMatchScores,
  createImmutableMatchTimer,
  createImmutableObjects,
} from './utils/immutable-structures';
import getMatchLines from './utils/match-lines';
// import updateStakeAndBetsNames from './utils/update-stake-and-bets-names';

// ---

export function transformMatchesSSRState(state: SSRMatchesState): RecordOfMatchesState {
  return transformNodeSSRState(state, initialState, (entry) => {
    const { bets, stakes } = transformStakesSSRState(entry.stakes, entry.bets);

    return MatchEntity({
      stakes,
      bets,
      id: entry.id,
      sportId: entry.sportId,
      matchClasses: entry.matchClasses,
      modelType: entry.modelType,
      tournamentUnionId: entry.tournamentUnionId,
      tournamentId: entry.tournamentId,
      tournamentStageId: entry.tournamentStageId,
      slotType: entry.slotType,
      type: entry.type,
      isOpen: entry.isOpen,
      lines: getMatchLines(entry.sportId, stakes, bets),
      name: entry.name,
      globalPriority: entry.globalPriority,
      betsCount: entry.betsCount,
      startDate: entry.startDate ? new Date(entry.startDate) : undefined,
      liveTracker: entry.liveTracker,
      state: isEmpty(entry.state) ? Map() : Map(entry.state),
      attributes: isEmpty(entry.attributes) ? Map() : Map(entry.attributes),
      status: entry.status,
      scores: createImmutableMatchScores(entry.scores),
      timer: createImmutableMatchTimer(entry.timer),
      objects: createImmutableMatchObjects(entry.objects),
      periods: createImmutableMatchPeriods(entry.periods),
    });
  });
}

// Match entities reducer.
function matchesReducer(
  state: Partial<MatchReducerState> | undefined,
  clientEntities: BackendEntities | undefined,
): MatchReducerState {
  let matchesState = state?.matchesState || initialState();
  let objectsState = state?.objectsState || objectInitialState();

  if (!clientEntities?.diff) {
    return createMatchesReducerResponse(matchesState, objectsState);
  }

  const matchIdsOfChangedObjects = new Set<number>();

  objectsState = objectsState.asMutable();
  objectsState = createObjectsState(objectsState, clientEntities.diff, (object) => {
    object.matchIds.forEach(matchIdsOfChangedObjects.add, matchIdsOfChangedObjects);
  });

  const { matches, removedMatches } = clientEntities.diff;

  let tempMatchesState = matchesState.get('byId');
  let tempMatchIds = matchesState.get('allIds');
  const lang = LanguageStore.getLang();

  if (!(matches || removedMatches)) {
    if (matchIdsOfChangedObjects.size > 0) {
      // Бесполезное действие при включении ANTLR закоментированно в связи с задачей - https://jira.tennisi.it/browse/CP-3498
      // tempMatchesState = updateStakeAndBetsNames(
      //   lang,
      //   tempMatchesState.asMutable(),
      //   objectsState,
      //   matchIdsOfChangedObjects,
      // );

      matchesState = matchesState.set('byId', tempMatchesState.asImmutable());
    }

    objectsState = clearEmptyObjects(objectsState);

    return createMatchesReducerResponse(matchesState, objectsState.asImmutable());
  }

  tempMatchesState = tempMatchesState.asMutable();
  tempMatchIds = tempMatchIds.asMutable();

  if (removedMatches) {
    const removedMatchesSet = new Set(removedMatches);

    tempMatchIds = tempMatchIds.filter((matchId) => !removedMatchesSet.has(matchId));

    removedMatches.forEach((matchId) => {
      const matchObjects = tempMatchesState.get(matchId)?.objects;

      matchObjects?.forEach((objectId) => {
        objectsState = removeMatchIdFromObjects(objectsState, objectId, matchId);
      });

      tempMatchesState.delete(matchId);
    });
  }

  if (matches) {
    Object.values(matches).forEach((backendMatch) => {
      const transformed = transformMatch(backendMatch);
      const matchId = transformed.id;

      if (typeof matchId !== 'number') {
        return;
      }

      const prevMatchValue = tempMatchesState.get(matchId);

      if (prevMatchValue) {
        if (transformed.matchClasses !== undefined) {
          tempMatchesState = updateField(tempMatchesState, matchId, 'matchClasses', transformed.matchClasses);
        }
        if (transformed.modelType !== undefined) {
          tempMatchesState = updateField(tempMatchesState, matchId, 'modelType', transformed.modelType);
        }
        if (transformed.isOpen !== undefined) {
          tempMatchesState = updateField(tempMatchesState, matchId, 'isOpen', transformed.isOpen);
        }
        if (transformed.name !== undefined) {
          tempMatchesState = updateField(tempMatchesState, matchId, 'name', transformed.name);
        }
        if (transformed.betsCount !== undefined) {
          tempMatchesState = updateField(tempMatchesState, matchId, 'betsCount', transformed.betsCount);
        }
        if (transformed.globalPriority !== undefined) {
          tempMatchesState = updateField(tempMatchesState, matchId, 'globalPriority', transformed.globalPriority);
        }
        if (transformed.status !== undefined) {
          tempMatchesState = updateField(tempMatchesState, matchId, 'status', transformed.status);
        }
        if (transformed.state !== undefined) {
          tempMatchesState = updateField(tempMatchesState, matchId, 'state', Map(transformed.state));
        }
        if (transformed.attributes !== undefined) {
          tempMatchesState = updateField(tempMatchesState, matchId, 'attributes', Map(transformed.attributes));
        }
        if (transformed.startDate !== undefined) {
          tempMatchesState = updateField(
            tempMatchesState,
            matchId,
            'startDate',
            transformed.startDate ? new Date(transformed.startDate) : undefined,
          );
        }
        if (transformed.liveTracker !== undefined) {
          tempMatchesState = updateField(tempMatchesState, matchId, 'liveTracker', transformed.liveTracker);
        }
        if (transformed.scores !== undefined) {
          tempMatchesState = updateField(
            tempMatchesState,
            matchId,
            'scores',
            createImmutableMatchScores(transformed.scores),
          );
        }
        if (transformed.timer !== undefined) {
          tempMatchesState = updateField(
            tempMatchesState,
            matchId,
            'timer',
            createImmutableMatchTimer(transformed.timer),
          );
        }
        if (transformed.periods !== undefined) {
          tempMatchesState = updateField(
            tempMatchesState,
            matchId,
            'periods',
            createImmutableMatchPeriods(transformed.periods),
          );
        }
        if (transformed.objects !== undefined) {
          const immutableObjects = createImmutableObjects(
            transformed.objects,
            objectsState.get('byId'),
            (objectId, teamId) => {
              let match = tempMatchesState.get(matchId);
              if (!match) {
                console.error('lib > betting-entities > reducers > matches > createImmutableObjects: `match` not found');
                return;
              }
              const oldObjectId = match.objects.get(teamId);

              if (typeof oldObjectId !== 'number') {
                console.error('matches reducer -> `oldObjectId` must be a number', { oldObjectId });
                return;
              }

              match = match.removeIn(['objects', teamId]);

              tempMatchesState = tempMatchesState.set(matchId, match);

              objectsState = removeMatchIdFromObjects(
                objectsState,
                oldObjectId,
                matchId,
                false,
              );

              objectsState = addMatchIdToObject(objectsState, objectId, matchId);
            },
          );

          const value = updateStakesNames(
            lang,
            immutableObjects,
            prevMatchValue.stakes,
            prevMatchValue.bets,
          );

          tempMatchesState = updateField(tempMatchesState, matchId, 'stakes', value.stakes);
          tempMatchesState = updateField(tempMatchesState, matchId, 'bets', value.bets);

          tempMatchesState = updateField(
            tempMatchesState,
            matchId,
            'objects',
            createImmutableMatchObjects(transformed.objects),
          );
        }
        if (
          transformed.stakes !== undefined
          || transformed.bets !== undefined
          || transformed.removedStakes !== undefined
          || transformed.removedBets !== undefined
        ) {
          // TODO: Не нужно создавать каждый раз immutableObjects
          // https://jira.tennisi.it/browse/WEB-1612
          const immutableObjects = createImmutableObjects(
            prevMatchValue.get('objects'),
            objectsState.get('byId'),
          );

          const value = stakesReducer(
            lang,
            transformed,
            {
              stakesState: prevMatchValue.stakes,
              betsState: prevMatchValue.bets,
              immutableObjects,
            },
          );

          const currentSportId = typeof transformed.sportId === 'number'
            ? transformed.sportId
            : prevMatchValue.sportId;

          const lines = getMatchLines(currentSportId, value.stakes, value.bets);

          tempMatchesState = updateField(tempMatchesState, matchId, 'stakes', value.stakes);
          tempMatchesState = updateField(tempMatchesState, matchId, 'bets', value.bets);
          tempMatchesState = updateField(tempMatchesState, matchId, 'lines', lines);
          tempMatchesState = updateField(tempMatchesState, matchId, 'betsCount', value.bets.size);
        }
      }
      else {
        const immutableObjects = createImmutableObjects(
          transformed.objects,
          objectsState.get('byId'),
          (objectId) => {
            objectsState = addMatchIdToObject(objectsState, objectId, matchId);
          },
        );

        const {
          stakes,
          bets,
        } = stakesReducer(lang, transformed, { immutableObjects });

        const entity = MatchEntity({
          ...transformed,
          stakes,
          bets,
          objects: createImmutableMatchObjects(transformed.objects),
          lines: getMatchLines(transformed.sportId, stakes, bets),
          timer: createImmutableMatchTimer(transformed.timer),
          periods: createImmutableMatchPeriods(transformed.periods),
          scores: createImmutableMatchScores(transformed.scores),
          startDate: transformed.startDate ? new Date(transformed.startDate) : undefined,
          state: transformed.state ? Map(transformed.state) : Map(),
          attributes: transformed.attributes ? Map(transformed.attributes) : Map(),
        });

        tempMatchIds = tempMatchIds.push(matchId);
        tempMatchesState = tempMatchesState.set(matchId, entity);
      }
    });
  }

  // Бесполезное действие при включении ANTLR закоментированно в связи с задачей - https://jira.tennisi.it/browse/CP-3498
  // if (matchIdsOfChangedObjects.size > 0) {
  //   tempMatchesState = updateStakeAndBetsNames(
  //     lang,
  //     tempMatchesState,
  //     objectsState,
  //     matchIdsOfChangedObjects,
  //   );
  // }

  objectsState = clearEmptyObjects(objectsState);

  matchesState = matchesState.set('byId', tempMatchesState.asImmutable());
  matchesState = matchesState.set('allIds', tempMatchIds.asImmutable());

  return createMatchesReducerResponse(matchesState, objectsState.asImmutable());
}

function createMatchesReducerResponse(
  matches: RecordOfMatchesState,
  objects: RecordOfObjectsState,
): MatchReducerState {
  return { matchesState: matches, objectsState: objects };
}

export default matchesReducer;
